import React, { useState } from 'react';
import requiredIf from 'react-required-if';
import PropTypes from 'prop-types';
import axios from 'axios';
import { isEmpty } from 'lodash';

import Chip from '~/components/core/Atomic/Chip/Chip';
import Grid from '~/components/core/Atomic/Grid/Grid';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import { FnolSubOrgLobFilterBar } from '~/components/Fnol/filters/FnolSubOrgLobFilterBar';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import useDebouncedValue from '~/hooks/useDebouncedValue';
import useIdBasedPagination from '~/hooks/useIdBasedPagination';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, reportAxiosError } from '~/Utils';

import { serverDateTimeToLocal } from '../../DateTimeUtils';
import { getLobIcon } from '../../Utils/lobUtils';
import WithConfirm from '../ConfirmModal';
import { LoadingSwitch, SortableTable } from '../core';
import OverflowArrayTextDisplayWithTooltip from '../core/OverflowArrayTextWithTooltip/OverflowArrayTextDisplayWithTooltip';
import SkeletonTable from '../core/Skeletons/SkeletonTable';
import { PAGINATION_LOCATION } from '../core/Tables/SortableTable/utils';
import Text from '../core/TextComponents/Text';
import { useCms } from '../hooks/useCms';
import { PencilIcon, TrashIcon } from '../icons';
import InlineIconButton from '../InlineIconButton';
import useOrganization from '../OrganizationContext';

import { useStyles } from '../../assets/styles';
import styles from './fnolDraft.module.scss';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_ORDER_BY = 'last_updated';
const DEFAULT_ORDER = 'desc';
const FETCH_IDS_URL = '/api/v1/fnol_drafts/item_ids';
const FETCH_PAGE_DATA_URL = '/api/v1/fnol_drafts/items_by_ids';
const FILTER_DEBOUNCE_MS = 250;
const SKELETON_COLUMNS_COUNT = 6;
// Columns whose server-side sort is supported (see SORTABLE_FNOL_DRAFT_COLUMNS on the backend).
const SORTABLE_COLUMN_IDS = new Set(['last_updated', 'datetime_created', 'policy_number', 'policy_lob']);

const FnolDraftsContainer = ({ onSelectDraft }) => {
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [subOrgIds, setSubOrgIds] = useState([]);
  const [lobs, setLobs] = useState([]);

  const sortByParam = `${order === 'asc' ? '+' : '-'}${orderBy}`;

  // Debounce filter values only — page/sort still fire immediately.
  const debouncedSubOrgIds = useDebouncedValue(subOrgIds, FILTER_DEBOUNCE_MS);
  const debouncedLobs = useDebouncedValue(lobs, FILTER_DEBOUNCE_MS);

  // Drafts are stored with policy-form LOB (e.g. 'auto_policy'); the shared filter emits claim-form (e.g. 'auto_claim').
  const policyLobs = debouncedLobs.map((lob) => lob.replace('_claim', '_policy'));

  const fetchIdsParams = {
    sort_by: sortByParam,
    ...(!isEmpty(debouncedSubOrgIds) && { sub_organization_ids: debouncedSubOrgIds }),
    ...(!isEmpty(policyLobs) && { lobs: policyLobs }),
  };

  const { pagination, pageResults, isLoading, isError, reload } = useIdBasedPagination({
    fetchIdsUrl: FETCH_IDS_URL,
    fetchPageDataUrl: FETCH_PAGE_DATA_URL,
    fetchIdsParams,
    initialRowsPerPage: DEFAULT_ROWS_PER_PAGE,
  });

  const paginationProps = {
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    count: pagination.totalCount,
    rowsPerPage: pagination.rowsPerPage,
    page: pagination.page,
    onChangePage: (_, newPage) => pagination.setPage(newPage),
    onChangeRowsPerPage: (e) => pagination.setRowsPerPage(parseInt(e.target.value, 10)),
  };

  const handleSortByColumn = (_, { id, order: newOrder }) => {
    if (!SORTABLE_COLUMN_IDS.has(id)) return;
    setOrderBy(id);
    setOrder(newOrder);
    pagination.setPage(0);
  };

  const handleSubOrgChange = (nextIds) => {
    setSubOrgIds(nextIds);
    pagination.setPage(0);
  };

  const handleLobChange = (nextLobs) => {
    setLobs(nextLobs);
    pagination.setPage(0);
  };

  const handleClearFilters = () => {
    setSubOrgIds([]);
    setLobs([]);
    pagination.setPage(0);
  };

  const hasFilters = !isEmpty(subOrgIds) || !isEmpty(lobs);
  const isInitialLoading = isLoading && pagination.totalCount === 0 && !hasFilters;

  if (isError) {
    return <LoadingSwitch isLoading={false} isError />;
  }
  if (!isInitialLoading && pagination.totalCount === 0 && !hasFilters) {
    return null;
  }

  return (
    <CollapsibleWrapper
      title="Saved Drafts"
      actionCard
      noBorder
      backgroundColor={CollapsibleWrapper.BACKGROUND_COLOR.WHITE}
    >
      {isInitialLoading ? (
        <SkeletonTable rowsCount={DEFAULT_ROWS_PER_PAGE} columnsCount={SKELETON_COLUMNS_COUNT} />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FnolSubOrgLobFilterBar
              subOrgIds={subOrgIds}
              lobs={lobs}
              onSubOrgChange={handleSubOrgChange}
              onLobChange={handleLobChange}
              onClear={handleClearFilters}
              showUnknownOption={false}
            />
          </Grid>
          <Grid item xs={12}>
            {isLoading ? (
              <SkeletonTable rowsCount={pagination.rowsPerPage} columnsCount={SKELETON_COLUMNS_COUNT} />
            ) : isEmpty(pageResults) ? (
              <div className="flex w-full justify-center py-32">
                <Text variant={Text.VARIANTS.LG} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                  No records found
                </Text>
              </div>
            ) : (
              <FnolDraftsTable
                drafts={pageResults}
                onSelectDraft={onSelectDraft}
                allowDiscard
                onDraftDiscarded={reload}
                paginationProps={paginationProps}
                onSortByColumn={handleSortByColumn}
                order={order}
                orderBy={orderBy}
              />
            )}
          </Grid>
        </Grid>
      )}
    </CollapsibleWrapper>
  );
};

FnolDraftsContainer.propTypes = {
  onSelectDraft: PropTypes.func.isRequired,
};

function FnolDraftsTable({
  drafts,
  onSelectDraft,
  onDraftDiscarded,
  allowDiscard,
  paginationProps,
  onSortByColumn,
  order,
  orderBy,
}) {
  const { classes } = useStyles();
  const { lobConfigurationsDict } = useLobConfiguration();
  const { subOrganizationEnabled, subOrganizations } = useOrganization();
  const { userOrganization } = useCms();
  const isCommercialPoliciesEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.COMMERCIAL_POLICIES
  );
  const isConfigurableFnolEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL);

  const handleDiscardDraft = async (fnolDraft) => {
    try {
      await axios.delete(`/api/v1/fnol_drafts/${fnolDraft.id}`);
      await onDraftDiscarded(fnolDraft);
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const handleSelectDraft = async (row) => {
    try {
      const { data: fullDraft } = await axios.get(`/api/v1/fnol_drafts/${row.id}`);
      await onSelectDraft(fullDraft);
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const columnData = [
    {
      id: 'policy_number',
      numeric: false,
      label: 'Policy #',
      nowrap: true,
      specialCell: (fnolDraft) => (
        <div className={styles.policyNumberTableCell}>
          {fnolDraft.policy_lob &&
            getLobIcon({ lob: fnolDraft.policy_lob.replace('_policy', '_claim'), lobConfigurationsDict })}
          {fnolDraft.policy_number}
        </div>
      ),
      width: isCommercialPoliciesEnabled && isConfigurableFnolEnabled ? '150px' : undefined,
    },
    {
      id: 'first_party_name_chip',
      label: 'First Party Involved',
      isHidden: !isCommercialPoliciesEnabled || !isConfigurableFnolEnabled,
      specialCell: (row) => {
        const firstPartyNames = row?.first_party_names || [];

        if (isEmpty(firstPartyNames)) return null;

        return (
          <div>
            <OverflowArrayTextDisplayWithTooltip
              value={firstPartyNames}
              renderItem={(label) => (
                <span key={label}>
                  <Chip label={label} size="small" className="mr-12" />
                </span>
              )}
            />
          </div>
        );
      },
      disableSort: true,
      width: '250px',
    },
    {
      id: 'sub_organization_name',
      numeric: false,
      label: 'Sub Organization',
      disableSort: true,
      specialCell: (row) => subOrganizations?.find((subOrg) => subOrg.id === row.sub_organization_id)?.name,
    },
    {
      id: 'last_updated',
      numeric: false,
      label: 'Last updated on',
      specialCell: (row) => serverDateTimeToLocal(row.last_updated),
    },
    { id: 'last_editing_adjuster', numeric: false, label: 'Last updated by', disableSort: true },
    {
      id: 'datetime_created',
      numeric: false,
      label: 'Created on',
      specialCell: (row) => serverDateTimeToLocal(row.datetime_created),
    },
    { id: 'creating_adjuster', numeric: false, label: 'Created by', disableSort: true },
    {
      id: 'actions',
      numeric: false,
      label: 'Actions',
      disableSort: true,
      specialCell: (row) => (
        <span style={{ display: 'inline-flex' }}>
          <InlineIconButton
            className={classes.hoverableNonFilledIcon}
            icon={PencilIcon}
            ariaLabel="Edit draft"
            onClick={async () => await handleSelectDraft(row)}
          />
          {allowDiscard && (
            <WithConfirm title="Discard draft?" primaryButtonName="Yes" shouldCloseOnPrimary={false}>
              <InlineIconButton
                className={classes.hoverableNonFilledIcon}
                icon={TrashIcon}
                ariaLabel="Discard draft"
                onClick={async () => await handleDiscardDraft(row)}
              />
            </WithConfirm>
          )}
        </span>
      ),
    },
  ];

  const visibleColumns = subOrganizationEnabled
    ? columnData
    : columnData.filter((col) => col.id !== 'sub_organization_name');

  const defaultOrderIdx = Math.max(
    0,
    visibleColumns.findIndex((column) => column.id === (orderBy ?? 'last_updated'))
  );

  return (
    <SortableTable
      rows={drafts}
      columns={visibleColumns}
      defaultOrderColumn={defaultOrderIdx}
      order={order ?? 'desc'}
      onSortByColumn={onSortByColumn}
      paginationProps={paginationProps}
      paginationLocation={PAGINATION_LOCATION.TOP}
    />
  );
}
FnolDraftsTable.propTypes = {
  drafts: PropTypes.array.isRequired,
  onSelectDraft: PropTypes.func.isRequired,
  allowDiscard: PropTypes.bool,
  onDraftDiscarded: requiredIf(PropTypes.func, (props) => props.allowDiscard),
  paginationProps: PropTypes.object,
  onSortByColumn: PropTypes.func,
  order: PropTypes.oneOf(['asc', 'desc']),
  orderBy: PropTypes.string,
};

export { FnolDraftsTable };
export default FnolDraftsContainer;
